// components/PhoneCaptureCard.js
// P2-B — Gate screen (v3 spec §3.2): add mobile number, shown post-auth when
// no phone is on file.
//
// Shown (via ONE conditional render in App.modular) when a signed-in user has
// no phone on their profile — typically after Google OAuth, which does not
// share a phone number. Writes phone (E.164) + the user's explicit A2P choice
// to user_profiles with sms_consent_source='google_phone_capture'.
//
// Per v3 the Gate is blocking: the exits are Continue (with a valid phone) or
// "Use a different sign-in method" (signs out). SMS consent remains OPTIONAL
// and the checkbox is UNCHECKED by default (owner ruling). No SMS is sent by
// this component.
//
// Original IP — LoadDash Services.

import React, { useState } from "react";
import { NAVY, BLUE, STEEL, MUTED, NON_AFFILIATION_DISCLAIMER } from "../constants";
import { isPlausiblePhone, toE164US } from "../utils/phoneCapture";
import { upsertOwnPhoneConsent } from "../services/profileService";

const A2P_TEXT = "By checking this box, I agree to receive pickup and account texts from LoadDash at the number provided. Message and data rates may apply. Message frequency varies. Reply STOP to opt out, HELP for help. SMS consent is not required to book service.";

export default function PhoneCaptureCard({ userId, email = "", provider = "", onDone, onSignOut }) {
  const [phone, setPhone] = useState("");
  const [smsConsent, setSmsConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function save() {
    if (!isPlausiblePhone(phone) || busy) return;
    // Store E.164 (+1XXXXXXXXXX); a plausible-length but non-US number is
    // rejected here rather than written raw.
    const e164 = toE164US(phone);
    if (!e164) { setErr("Enter a valid US mobile number."); return; }
    setErr(""); setBusy(true);
    try {
      const { error } = await upsertOwnPhoneConsent({
        userId, phone: e164, smsConsent, source: "google_phone_capture",
      });
      if (error) { setErr("Could not save your number. Please try again."); setBusy(false); return; }
      onDone();
    } catch (e) {
      setErr("Could not save your number. Please try again.");
      setBusy(false);
    }
  }

  const ready = isPlausiblePhone(phone) && !busy;
  const initial = (email || "?").trim().charAt(0).toUpperCase();
  const signedInLabel = provider === "google" ? "Signed in with Google" : "Signed in";

  return (
    <div style={{ position: "fixed", inset: 0, background: "#DCE3E7", zIndex: 1000, display: "flex", flexDirection: "column", overflowY: "auto", padding: 16, boxSizing: "border-box", fontFamily: "'Sora',system-ui,sans-serif" }}>
      <style>{`.ld-input:focus{border-color:#3D90D9 !important;box-shadow:0 0 0 3px rgba(61,144,217,.16)}`}</style>
      {/* v3 §2.3 card: 400px, radius 28, hairline border, deep soft shadow */}
      <div style={{ background: "#F5F7F9", width: "100%", maxWidth: 400, margin: "auto", borderRadius: 28, border: "1px solid rgba(10,22,40,0.06)", boxShadow: "0 40px 80px -30px rgba(10,22,40,0.35)", padding: "20px 20px 26px", boxSizing: "border-box" }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingBottom: 12 }}>
          <img src="/loaddash-logo.png" alt="LoadDash" style={{ height: 60, width: "auto", maxWidth: "55%", objectFit: "contain", marginBottom: 6 }} onError={e => { e.target.style.display = "none"; }} />
          <div style={{ fontSize: 12, color: STEEL, fontWeight: 600 }}>One more step before you can book</div>
        </div>
        {/* v3 §3.2 identity row */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#fff", border: "1px solid #E3E9ED", borderRadius: 12, padding: "10px 12px", marginBottom: 16 }}>
          <div aria-hidden="true" style={{ width: 34, height: 34, borderRadius: "50%", background: "linear-gradient(135deg,#03A66A,#04c37d)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 15, flexShrink: 0 }}>{initial}</div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: 11, color: STEEL, fontWeight: 700 }}>{signedInLabel}</div>
            <div style={{ fontSize: 13, color: NAVY, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email}</div>
          </div>
          <span aria-hidden="true" style={{ color: "#03A66A", fontWeight: 800, fontSize: 15, flexShrink: 0 }}>✓</span>
        </div>
        <div style={{ fontSize: 19, fontWeight: 700, color: NAVY, marginBottom: 6 }}>Add your mobile number</div>
        <div style={{ fontSize: 13, color: STEEL, lineHeight: 1.5, marginBottom: 14 }}>
          Haulers use this to reach you at pickup, and it's how we send booking confirmations. A mobile number is required to book.
        </div>
        <label htmlFor="gate-phone" style={{ fontSize: 11.5, fontWeight: 600, color: STEEL, display: "block", marginBottom: 6, letterSpacing: "0.03em", textTransform: "uppercase" }}>Mobile Number</label>
        <input
          id="gate-phone"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          placeholder="(801) 555-0134"
          type="tel"
          autoComplete="tel"
          className="ld-input"
          style={{ width: "100%", background: "#fff", border: "1px solid #C7D1D7", borderRadius: 10, color: NAVY, fontSize: 15, padding: "13px 14px", outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 13 }}
        />
        <label style={{ display: "flex", gap: 9, alignItems: "flex-start", marginBottom: 14, cursor: "pointer" }}>
          <input type="checkbox" checked={smsConsent} onChange={e => setSmsConsent(e.target.checked)} style={{ width: 17, height: 17, marginTop: 1, flexShrink: 0, accentColor: BLUE }} />
          <span style={{ fontSize: 11, color: STEEL, lineHeight: 1.5 }}>{A2P_TEXT} <a href="/sms-consent" style={{ color: BLUE, fontWeight: 700 }}>SMS Terms</a></span>
        </label>
        {err && <div style={{ background: "#FBEAEA", border: "1px solid #EAB8B8", borderRadius: 8, padding: "9px 12px", fontSize: 12, color: "#B03A3A", marginBottom: 12 }}>{err}</div>}
        <button onClick={save} disabled={!ready} style={{ width: "100%", padding: "15px", minHeight: 48, borderRadius: 10, border: "none", background: ready ? BLUE : "#b0bec5", color: "#fff", fontWeight: 600, fontSize: 15.5, cursor: ready ? "pointer" : "not-allowed", opacity: busy ? 0.6 : 1, fontFamily: "inherit" }}>
          {busy ? "Saving…" : "Continue"}
        </button>
        <div style={{ textAlign: "center", marginTop: 10 }}>
          <button onClick={onSignOut || onDone} style={{ background: "none", border: "none", color: MUTED, fontSize: 12, fontWeight: 700, cursor: "pointer", minHeight: 44, fontFamily: "inherit" }}>← Use a different sign-in method</button>
        </div>
        <div style={{ borderTop: "1px solid #E3E9ED", marginTop: 18, paddingTop: 14, fontSize: 11, color: "#94A6B1", lineHeight: 1.6, textAlign: "center" }}>{NON_AFFILIATION_DISCLAIMER}</div>
      </div>
    </div>
  );
}
